import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRight, PlayCircle, Loader2 } from "lucide-react";
import { api, assetUrl } from "@/lib/api";
import { hubAccess, rememberHubEmail } from "@/lib/hubAccess";
import { CardMedia, HubReader } from "@/components/HubReader";
import { PublicError } from "@/components/PublicShell";
import { useFonts } from "@/components/LandingPage";

const contentPath = (it) => `/c/${it.slug}/${it.campaign_type === "whitepaper" ? "read" : "watch"}`;

export default function Hub() {
  const { hubSlug } = useParams();
  const nav = useNavigate();
  const [h, setH] = useState(null);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState("");
  useEffect(() => {
    rememberHubEmail(hubSlug);
    api.get(`/public/hubs/${hubSlug}`).then((r) => { setH(r.data); document.title = r.data.hub.headline || r.data.name; }).catch(() => setMissing(true));
  }, [hubSlug]);
  useFonts(h?.hub?.font_heading, "DM Sans");

  const open = async (it) => {
    setBusy(it.slug);
    try {
      if (await hubAccess(hubSlug, h.items, it)) return nav(contentPath(it));
    } catch { /* fall through to the landing page */ } finally { setBusy(""); }
    if (it.hub_only) return;
    nav(`/c/${it.slug}?hub=${hubSlug}`);
  };

  if (missing) return <PublicError text="This content hub isn't available." />;
  if (!h) return <div className="min-h-screen grid place-items-center bg-[#070709]"><Loader2 className="w-6 h-6 animate-spin text-pink-400" /></div>;
  const b = h.hub;
  const light = b.theme === "light";
  const accent = b.accent || "#EC4899";
  const dim = light ? "text-slate-500" : "text-slate-400";
  if (b.layout === "reader") {
    return (
      <div className={`min-h-screen ${light ? "text-slate-900" : "text-white"}`} style={{ backgroundColor: b.bg_color || "#070709", fontFamily: "'DM Sans', sans-serif" }} data-testid="hub-page">
        <HubReader h={h} hubSlug={hubSlug} />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${light ? "text-slate-900" : "text-white"}`} style={{ backgroundColor: b.bg_color || "#070709", fontFamily: "'DM Sans', sans-serif" }} data-testid="hub-page">
      <div className="max-w-6xl mx-auto px-6 py-14">
        <div className="flex items-center justify-between mb-16">
          {b.logo_path ? <img src={assetUrl(b.logo_path)} alt="Logo" className="h-10 w-auto object-contain" data-testid="hub-page-logo" /> : <span className="font-semibold">{h.client_name}</span>}
          <span className={`font-mono text-[11px] ${dim}`}>{h.code}</span>
        </div>
        <p className="font-mono text-xs uppercase tracking-[0.3em] mb-5" style={{ color: accent }}>{h.client_name || "Content hub"}</p>
        <h1 data-testid="hub-page-headline" className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] max-w-3xl" style={{ fontFamily: `"${b.font_heading || "Outfit"}", sans-serif` }}>{b.headline || h.name}</h1>
        {b.message && <p data-testid="hub-page-message" className={`text-lg mt-6 max-w-2xl ${light ? "text-slate-600" : "text-slate-300"}`}>{b.message}</p>}

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-16" data-testid="hub-items">
          {h.items.length === 0 && <p className={dim}>New content is coming soon.</p>}
          {h.items.map((it) => (
            <button key={it.slug} data-testid={`hub-item-${it.slug}`} onClick={() => open(it)}
              className={`group text-left flex flex-col justify-start rounded-2xl border overflow-hidden transition-transform hover:-translate-y-1 ${light ? "bg-white border-slate-200 shadow-sm" : "bg-white/[0.04] border-white/10"}`}>
              <div className="w-full aspect-[16/9] relative bg-black/30 overflow-hidden">
                <CardMedia it={it} accent={accent} />
                {it.campaign_type !== "whitepaper" && <span className="absolute inset-0 grid place-items-center pointer-events-none"><PlayCircle className="w-12 h-12 text-white/90 drop-shadow-lg" /></span>}
                <span className="absolute bottom-3 left-3 text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full bg-black/70 backdrop-blur-sm text-white">
                  {it.campaign_type === "whitepaper" ? "White paper" : it.hub_only ? "Video" : "Webinar"}
                </span>
              </div>
              <div className="p-5">
                <h3 className="font-semibold text-lg leading-snug" style={{ fontFamily: `"${b.font_heading || "Outfit"}", sans-serif` }}>{it.title}</h3>
                {it.headline && <p className={`text-sm mt-2 line-clamp-2 ${dim}`}>{it.headline}</p>}
                <span className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold" style={{ color: accent }}>
                  {busy === it.slug ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{it.cta_text || "Access now"} <ArrowRight className="w-4 h-4" /></>}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
